import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface MonthlySpend {
  /** `YYYY-MM`. */
  month: string;
  assetSpend: string;
  repairSpend: string;
  assetCount: number;
  repairCount: number;
}

export interface CategorySpend {
  name: string;
  total: string;
  count: number;
}

export interface SpendSummary {
  assets: {
    /** All time. Includes assets since retired or disposed of - the money was still
     *  spent, and a figure that shrinks when you scrap something cannot be reconciled
     *  against the finance ledger. */
    total: string;
    /** Purchase cost of everything NOT retired or disposed of. */
    inService: string;
    /** Assets whose price is recorded - the totals cover only these. */
    priced: number;
    missingPrice: number;
    /** How many are archived. The gap between the two totals. */
    archived: number;
  };
  repairs: { total: string; count: number; open: number };
  months: MonthlySpend[];
  topCategories: CategorySpend[];
  windowStart: string;
  windowEnd: string;
}

/**
 * Money arrives as STRINGS, not numbers.
 *
 * These are Postgres numerics summed across the whole register; through
 * JSON.stringify a large total would lose precision, and this is the one screen where
 * the figure has to reconcile against finance's own.
 */
export function useSpendSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['spend'] as const,
    queryFn: () => api.get<SpendSummary>('/spend'),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}
